import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { UtensilsCrossed } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/health").catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    const MAX = 4;
    try {
      for (let attempt = 1; attempt <= MAX; attempt++) {
        try {
          await login(email, password);
          toast.success("Accesso effettuato");
          navigate("/");
          return;
        } catch (err) {
          const status = err.response?.status;
          // Credenziali errate o richiesta non valida: stop immediato
          if (status === 401 || status === 403) {
            toast.error(formatApiError(err.response?.data?.detail) || "Credenziali non valide");
            return;
          }
          // Errore temporaneo (server in avvio / rete): riprova
          const transient = !err.response || status >= 500;
          if (transient && attempt < MAX) {
            if (attempt === 1) toast.loading("Il server si sta avviando, un attimo…", { id: "login-retry" });
            await sleep(1200 * attempt);
            continue;
          }
          toast.dismiss("login-retry");
          toast.error(!err.response
            ? "Impossibile contattare il server. Controlla la connessione e riprova."
            : (formatApiError(err.response?.data?.detail) || "Il server non risponde al momento. Riprova tra qualche secondo."));
          return;
        }
      }
    } finally {
      toast.dismiss("login-retry");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img
          src="https://images.pexels.com/photos/2387675/pexels-photo-2387675.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Interno locale"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
          <img src="/logo-dogana.png" alt="Dogana 2.0" className="h-14 w-auto mb-6 drop-shadow-lg" />
          <h1 className="font-display text-5xl font-light leading-tight">Gestionale<br />del tuo Locale</h1>
          <p className="mt-4 text-white/80 max-w-md">Menù digitale, prenotazioni, magazzino, HACCP e contabilità in un unico posto.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 animate-fade-up" data-testid="login-form">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-primary">Area riservata</p>
            <h2 className="font-dash text-3xl font-semibold mt-2">Accedi allo staff</h2>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="nome@locale.it" required data-testid="login-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" required data-testid="login-password-input" />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full h-11" data-testid="login-submit-button">
            {loading ? "Accesso…" : "Entra"}
          </Button>
          <a href="/menu" className="block text-center text-sm text-muted-foreground hover:text-primary transition-colors" data-testid="view-public-menu-link">
            Vedi il menù pubblico →
          </a>
        </form>
      </div>
    </div>
  );
}
